import { type merged_time_and_activity } from '@common/@types/prisma';
import { type StudentScheduleSlot, type UIStudentScheduleSlotMap } from '../../model';

import { convertToNearestMultiple, ONE_DOT_ANGLE } from './commonUtils';
import { l2_schedule, SUBJECT_NAMES, MAP_EMPTY_SUBJECT_NAMES } from '../data';

const TOTAL_CIRCLE_ANGLE = 360;
const TOTAL_SEMI_CIRCLE_ANGLE = TOTAL_CIRCLE_ANGLE / 2;

type ScheduleType = 'l2';

/**
 * Check if passed in string is not a learning session
 */
export const isNonLearningSlot = (label?: string) => {
  if (!label || label === 'Break' || label === 'Lunch') {
    return true;
  }

  return false;
};

/*
  Return student schedule data based on passed in level
*/
export const getSchedule = (scheduleType: ScheduleType): StudentScheduleSlot[] => {
  const dataMap = {
    l2: l2_schedule,
  } as const;

  const selectedSchedule = dataMap[scheduleType];
  const transformedData = convertDatesToToday(selectedSchedule);

  return transformedData;
};

/**
 * Convert hardcoded schedule startTime and endTime to todays date without changing the time.
 */
export const convertDatesToToday = (data: StudentScheduleSlot[]): StudentScheduleSlot[] => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();

  const result: StudentScheduleSlot[] = [];

  data.forEach((slot) => {
    const start = new Date(slot.startTime);
    start.setFullYear(year);
    start.setMonth(month);
    start.setDate(day);

    const end = new Date(slot.endTime);
    end.setFullYear(year);
    end.setMonth(month);
    end.setDate(day);

    result.push({
      ...slot,
      startTime: start,
      endTime: end,
    });
  });

  return result;
};

/**
 * Convert student day class schedule data for UI representation
 */
export const convertScheduleForUI = (data: Array<StudentScheduleSlot>, addLastItem?: boolean) => {
  const startAngle = 270;
  const results: UIStudentScheduleSlotMap = {};

  data.forEach((slot, i) => {
    const angle = (startAngle + (i * TOTAL_SEMI_CIRCLE_ANGLE) / data.length) % TOTAL_CIRCLE_ANGLE;
    const roundedAngle = convertToNearestMultiple(angle, ONE_DOT_ANGLE);

    results[roundedAngle] = {
      ...slot,
      angle: roundedAngle,
    };
  });

  // add end time (i.e 3:00 PM) for UI
  if (addLastItem) {
    results['90'] = {
      slotLabel: '',
      angle: 90,
      startTime: new Date('01/01/2024 3:00 PM'),
      endTime: new Date('01/01/2024 9:00 AM'),
      startTimeLabel: '3:00 PM',
      endTimeLabel: '9:00 PM',
      interactive: false,
    };
  }

  return results;
};

/**
 * Convert and Sort student schedule to ui representation
 */
export const getSortedSchedule = (data: Array<StudentScheduleSlot>) => {
  const transformedData = convertScheduleForUI(data, false);
  const sortedData = Object.values(transformedData).sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  return sortedData;
};

/**
 * Get array of non repeated learning schedule excluding any breaks
 * (i.e Learning sessions before Lunch)
 */
export const getFirstHalfLearningSchedule = (data: Array<StudentScheduleSlot>) => {
  const results: Record<string, StudentScheduleSlot> = {};

  if (!data) {
    return results;
  }

  for (let i = 0; i < data.length; i++) {
    const schedule = data[i];
    const label = schedule.slotLabel;
    const key = label.toLowerCase();

    if (isNonLearningSlot(label) || schedule.repeatedClass || results.hasOwnProperty(key)) {
      continue;
    }

    results[key] = schedule;
  }

  return results;
};

/**
 * Get Student Learning Data for provided date
 */
export const getLearningDataForDate = (
  data: Array<merged_time_and_activity> | undefined,
  date: string
): Array<merged_time_and_activity> => {
  if (!data || !data.length) {
    return [];
  }

  return data.filter((item) => item.date === date);
};

/**
 * Get unique subjects name list from given student activity data
 */
export const getUniqueSubjectNames = (
  data: Array<merged_time_and_activity> | undefined
): Array<string> => {
  if (!data || !data.length) {
    return [];
  }

  return data.map((item) => item.subject);
};

/**
 * Convert hardcoded student schedule labels by mapping it to subjects data returned from server
 */
export const transformScheduleData = (
  schedule: StudentScheduleSlot[],
  subjects: string[] | undefined
) => {
  if (!subjects || !subjects.length) {
    return mapEmptySubjectNames(schedule);
  }

  const isBreakSlotPresent = subjects.length <= 4;
  const numOfSubjects = isBreakSlotPresent ? 4 : 5;
  const scheduleSubjects = getSubjectsForSchedule(subjects, numOfSubjects);

  const replacedSubjectNameMap: Record<string, string> = {};

  // map server response subjects to schedule slots
  const newSchedule = schedule.map((slot, index) => {
    const { slotLabel, repeatedClass } = slot;
    const currentSubject = scheduleSubjects[index];

    if (isNonLearningSlot(slotLabel)) {
      if (isBreakSlotPresent) {
        return slot;
      }

      if (replacedSubjectNameMap['break']) {
        return {
          ...slot,
          slotLabel: replacedSubjectNameMap['break'],
          repeatedClass: true,
          interactive: true,
        };
      }

      replacedSubjectNameMap['break'] = currentSubject;
      return {
        ...slot,
        interactive: true,
        slotLabel: replacedSubjectNameMap['break'],
      };
    }

    if (repeatedClass) {
      return {
        ...slot,
        slotLabel: replacedSubjectNameMap[slotLabel],
      };
    }

    replacedSubjectNameMap[slotLabel] = currentSubject;

    return {
      ...slot,
      slotLabel: currentSubject,
    };
  });

  return newSchedule;
};

/**
 * Used when server returns no response or is empty
 * Mapped empty slot labels (i.e slot1, slot2..etc) with actual subject names
 */
const mapEmptySubjectNames = (schedule: StudentScheduleSlot[]): StudentScheduleSlot[] =>
  schedule.map((slot) => {
    if (MAP_EMPTY_SUBJECT_NAMES[slot.slotLabel]) {
      return {
        ...slot,
        slotLabel: MAP_EMPTY_SUBJECT_NAMES[slot.slotLabel],
      };
    }
    return slot;
  });

export const getSubjectsForSchedule = (
  subjectsArray: string[] | null,
  numSubjects: number
): string[] => {
  if (!subjectsArray) {
    return SUBJECT_NAMES.slice(0, numSubjects);
  }

  const subjectsToReturn: string[] = subjectsArray.slice(0, numSubjects);
  const remainingSubjects: string[] = SUBJECT_NAMES.filter(
    (subject) => !subjectsToReturn.includes(subject)
  );

  return [
    ...subjectsToReturn,
    ...remainingSubjects.slice(0, numSubjects - subjectsToReturn.length),
  ];
};
